import type { TranscriptExample } from './TranscriptMock';

/*
 * Example transcripts for the landing pages' hero. Everything is fictional.
 * Lines and notes are English keys, translated on render; file names and
 * people's names are not.
 */

const TEAM = ['Maya', 'Daniel', 'Sofia', 'Arjun'];

export const HUB_EXAMPLE: TranscriptExample = {
  file: 'weekly-team-sync.m4a',
  source: 'file',
  language: 'English',
  duration: '32:18',
  speakers: TEAM,
  lines: [
    {
      speaker: 0,
      time: '0:04',
      text: 'Let’s start with the launch. Are we still on track for the fifteenth?',
    },
    {
      speaker: 1,
      time: '0:11',
      text: 'Yes. The app is ready, we’re only waiting for the final review.',
    },
    {
      speaker: 0,
      time: '0:19',
      text: 'Great. Then I’ll send the announcement to customers on Monday.',
    },
    {
      speaker: 2,
      time: '0:26',
      text: 'I’ll update the help center before it goes out.',
    },
  ],
  playing: 1,
  insight: {
    title: 'Action Items',
    kind: 'checks',
    items: [
      { text: 'Maya sends the customer announcement on Monday' },
      { text: 'Sofia updates the help center before launch' },
      { text: 'Daniel confirms the final review by Friday' },
    ],
  },
};

export const AUDIO_EXAMPLE: TranscriptExample = {
  file: 'client-call-recording.mp3',
  source: 'file',
  language: 'English',
  duration: '18:42',
  speakers: ['Laura', 'Ben'],
  lines: [
    {
      speaker: 0,
      time: '0:03',
      text: 'Thanks for making time today. How did the first month go?',
    },
    {
      speaker: 1,
      time: '0:09',
      text: 'Really well. The team uses it every day, but reporting takes too long.',
    },
    {
      speaker: 0,
      time: '0:17',
      text: 'That’s useful to hear. Which reports do you build most often?',
    },
    {
      speaker: 1,
      time: '0:23',
      text: 'The weekly sales summary. It still takes us about two hours.',
    },
  ],
  playing: 1,
  insight: {
    title: 'Summary',
    kind: 'bullets',
    items: [
      { text: 'The team uses the product daily after one month' },
      { text: 'The weekly sales report takes about two hours to build' },
      { text: 'Laura will share a faster reporting template' },
    ],
  },
};

export const VIDEO_EXAMPLE: TranscriptExample = {
  file: 'product-demo-final.mp4',
  source: 'file',
  language: 'English',
  duration: '12:07',
  speakers: ['Priya', 'Tom'],
  lines: [
    {
      speaker: 0,
      time: '0:02',
      text: 'In this demo I’ll show you how to set up your first project.',
    },
    {
      speaker: 0,
      time: '0:08',
      text: 'Click New project, give it a name and choose a template.',
    },
    {
      speaker: 1,
      time: '2:41',
      text: 'Can you invite people before the project is finished?',
    },
    {
      speaker: 0,
      time: '2:46',
      text: 'Yes, and they only see the parts you share with them.',
    },
  ],
  playing: 1,
  insight: {
    title: 'Chapters',
    kind: 'chapters',
    items: [
      { time: '0:00', text: 'Creating your first project' },
      { time: '2:40', text: 'Inviting your team' },
      { time: '7:15', text: 'Sharing and permissions' },
    ],
  },
};

export const MP3_EXAMPLE: TranscriptExample = {
  file: 'founder-interview-raw.mp3',
  source: 'file',
  language: 'English',
  duration: '34:50',
  speakers: ['Jordan', 'Elena'],
  lines: [
    {
      speaker: 0,
      time: '0:05',
      text: 'What made you start the company in the first place?',
    },
    {
      speaker: 1,
      time: '0:10',
      text: 'I was running a bakery and spent every evening on paperwork.',
    },
    {
      speaker: 1,
      time: '0:17',
      text: 'So I built a simple tool for myself, and other owners asked for it.',
    },
    {
      speaker: 0,
      time: '0:25',
      text: 'And how many bakeries use it today?',
    },
  ],
  playing: 1,
  insight: {
    title: 'Summary',
    kind: 'bullets',
    items: [
      { text: 'Elena started the company to cut her own paperwork' },
      { text: 'Other bakery owners asked to use her tool' },
      { text: 'The interview covers growth, hiring and pricing' },
    ],
  },
};

export const MP4_EXAMPLE: TranscriptExample = {
  file: 'webinar-recording.mp4',
  source: 'file',
  language: 'English',
  duration: '56:30',
  speakers: ['Rachel', 'Marcus'],
  lines: [
    {
      speaker: 0,
      time: '0:12',
      text: 'Welcome, everyone. Today we’re looking at three ways to cut your cloud bill.',
    },
    {
      speaker: 0,
      time: '0:20',
      text: 'The first one is the easiest: switch off what nobody uses.',
    },
    {
      speaker: 1,
      time: '14:02',
      text: 'Does that work for servers that only run at night?',
    },
    {
      speaker: 0,
      time: '14:08',
      text: 'It does. Schedule them, and you only pay for the hours they run.',
    },
  ],
  playing: 1,
  insight: {
    title: 'Chapters',
    kind: 'chapters',
    items: [
      { time: '0:00', text: 'Switching off unused resources' },
      { time: '13:50', text: 'Scheduling night jobs' },
      { time: '31:20', text: 'Picking the right plan' },
    ],
  },
};

export const WAV_EXAMPLE: TranscriptExample = {
  file: 'site-visit-interview.wav',
  source: 'file',
  language: 'English',
  duration: '24:11',
  speakers: ['Hannah', 'Omar'],
  lines: [
    {
      speaker: 0,
      time: '0:06',
      text: 'Can you walk me through what happens when a delivery arrives?',
    },
    {
      speaker: 1,
      time: '0:12',
      text: 'We scan every box, then it waits by the door until someone is free.',
    },
    {
      speaker: 0,
      time: '0:20',
      text: 'How long does it usually wait there?',
    },
    {
      speaker: 1,
      time: '0:24',
      text: 'On busy days, most of the morning.',
    },
  ],
  playing: 1,
  insight: {
    title: 'Summary',
    kind: 'bullets',
    items: [
      { text: 'Every delivery is scanned when it arrives' },
      { text: 'Boxes wait by the door until staff are free' },
      { text: 'On busy days the wait lasts most of the morning' },
    ],
  },
};

export const M4A_EXAMPLE: TranscriptExample = {
  file: 'discovery-call.m4a',
  source: 'file',
  language: 'English',
  duration: '21:05',
  speakers: ['Nina', 'Carlos'],
  lines: [
    {
      speaker: 0,
      time: '0:04',
      text: 'What would you like to have solved in the next three months?',
    },
    {
      speaker: 1,
      time: '0:09',
      text: 'Our orders come in from three places, and we copy them by hand.',
    },
    {
      speaker: 0,
      time: '0:17',
      text: 'So one place for every order would already save time?',
    },
    {
      speaker: 1,
      time: '0:21',
      text: 'Hours every week. That’s the main thing.',
    },
  ],
  playing: 1,
  insight: {
    title: 'Next steps',
    kind: 'checks',
    items: [
      { text: 'Nina sends a proposal by Thursday' },
      { text: 'Carlos shares a sample of last month’s orders' },
      { text: 'Follow-up call booked for next week' },
    ],
  },
};

export const MEETING_EXAMPLE: TranscriptExample = {
  file: 'Product weekly sync',
  translateFile: true,
  source: 'recording',
  language: 'English',
  duration: '45:20',
  speakers: TEAM,
  lines: [
    {
      speaker: 0,
      time: '0:03',
      text: 'Quick update before we start: the beta opens to everyone on Monday.',
    },
    {
      speaker: 3,
      time: '0:10',
      text: 'Support is ready. We have answers for the ten most common questions.',
    },
    {
      speaker: 1,
      time: '0:18',
      text: 'Then let’s decide on pricing today, so the page is ready too.',
    },
    {
      speaker: 0,
      time: '0:25',
      text: 'Agreed. We keep the current price and test a yearly discount.',
    },
  ],
  playing: 3,
  insight: {
    title: 'Action Items',
    kind: 'checks',
    items: [
      { text: 'Daniel updates the pricing page by Friday' },
      { text: 'Arjun shares the support answers with the team' },
      { text: 'Maya sets up the yearly discount test' },
    ],
  },
};

export const PODCAST_EXAMPLE: TranscriptExample = {
  file: 'Episode 42: The psychology of pricing',
  translateFile: true,
  source: 'file',
  language: 'English',
  duration: '48:15',
  speakers: ['Sam', 'Grace'],
  lines: [
    {
      speaker: 0,
      time: '0:01',
      text: 'Welcome back to the show. Today Grace explains why prices end in nine.',
    },
    {
      speaker: 1,
      time: '0:08',
      text: 'Our brains read the first digit, so 9.99 feels much closer to 9 than to 10.',
    },
    {
      speaker: 0,
      time: '0:16',
      text: 'Does it still work when everyone knows the trick?',
    },
    {
      speaker: 1,
      time: '0:21',
      text: 'Surprisingly, yes. Knowing about it doesn’t switch it off.',
    },
  ],
  playing: 1,
  insight: {
    title: 'Show Notes',
    kind: 'chapters',
    items: [
      { time: '0:00', text: 'Why prices end in nine' },
      { time: '12:30', text: 'Anchoring and the decoy option' },
      { time: '31:45', text: 'Free trials versus discounts' },
    ],
  },
};

export const LECTURE_EXAMPLE: TranscriptExample = {
  file: 'Lecture 7: Cell biology',
  translateFile: true,
  source: 'recording',
  language: 'English',
  duration: '1:12:40',
  speakers: ['Dr. Patel', 'Leo'],
  lines: [
    {
      speaker: 0,
      time: '0:02',
      text: 'Today we look at how cells turn food into energy.',
    },
    {
      speaker: 0,
      time: '0:09',
      text: 'Most of that work happens in the mitochondria. This will be on the exam.',
    },
    {
      speaker: 1,
      time: '18:36',
      text: 'Do plant cells have mitochondria too?',
    },
    {
      speaker: 0,
      time: '18:40',
      text: 'They do, alongside chloroplasts. Good question.',
    },
  ],
  playing: 1,
  insight: {
    title: 'Key points',
    kind: 'bullets',
    items: [
      { text: 'Cells turn food into energy in the mitochondria' },
      { text: 'Plant cells have both mitochondria and chloroplasts' },
      { text: 'Mitochondria will be on the exam' },
    ],
  },
};

export const YOUTUBE_EXAMPLE: TranscriptExample = {
  file: 'How to start running: a beginner’s guide',
  translateFile: true,
  source: 'youtube',
  language: 'English',
  duration: '14:22',
  speakers: ['Mia'],
  lines: [
    {
      speaker: 0,
      time: '0:00',
      text: 'If you’ve never run before, this video is for you.',
    },
    {
      speaker: 0,
      time: '0:06',
      text: 'Start with one minute of running and two minutes of walking.',
    },
    {
      speaker: 0,
      time: '4:12',
      text: 'Repeat that eight times, three days a week.',
    },
    {
      speaker: 0,
      time: '9:30',
      text: 'After four weeks, most people can run for ten minutes without stopping.',
    },
  ],
  playing: 1,
  insight: {
    title: 'Chapters',
    kind: 'chapters',
    items: [
      { time: '0:00', text: 'Your first run' },
      { time: '4:10', text: 'A simple weekly plan' },
      { time: '9:25', text: 'Running ten minutes without stopping' },
    ],
  },
};

export const TIKTOK_EXAMPLE: TranscriptExample = {
  file: '3 easy ways to save money every month',
  translateFile: true,
  source: 'tiktok',
  language: 'English',
  duration: '0:58',
  speakers: ['Lena'],
  lines: [
    {
      speaker: 0,
      time: '0:00',
      text: 'Three things I did that save me money every single month.',
    },
    {
      speaker: 0,
      time: '0:04',
      text: 'One: cancel every subscription you haven’t opened in thirty days.',
    },
    {
      speaker: 0,
      time: '0:15',
      text: 'Two: move your savings out on payday, before you can spend it.',
    },
    {
      speaker: 0,
      time: '0:29',
      text: 'Three: wait a day before buying anything over fifty dollars.',
    },
  ],
  playing: 1,
  insight: {
    title: 'Key points',
    kind: 'bullets',
    items: [
      { text: 'Cancel subscriptions you haven’t used in 30 days' },
      { text: 'Move savings out on payday' },
      { text: 'Wait a day before bigger purchases' },
    ],
  },
};

export const INSTAGRAM_EXAMPLE: TranscriptExample = {
  file: 'Easy 10-minute lemon pasta',
  translateFile: true,
  source: 'instagram',
  language: 'English',
  duration: '1:14',
  speakers: ['Marco'],
  lines: [
    {
      speaker: 0,
      time: '0:00',
      text: 'This lemon pasta takes ten minutes and five ingredients.',
    },
    {
      speaker: 0,
      time: '0:05',
      text: 'While the pasta cooks, mix lemon zest, juice and parmesan in a bowl.',
    },
    {
      speaker: 0,
      time: '0:21',
      text: 'Keep a cup of the pasta water. That’s the secret.',
    },
    {
      speaker: 0,
      time: '0:38',
      text: 'Toss everything together and add the water a little at a time.',
    },
  ],
  playing: 1,
  insight: {
    title: 'Summary',
    kind: 'bullets',
    items: [
      { text: 'A five-ingredient lemon pasta ready in ten minutes' },
      { text: 'Lemon, parmesan and pasta water make the sauce' },
      { text: 'Add the pasta water slowly until it’s creamy' },
    ],
  },
};

export const ZOOM_EXAMPLE: TranscriptExample = {
  file: 'GMT20261009-140512_Recording.mp4',
  source: 'file',
  language: 'English',
  duration: '41:07',
  speakers: ['Maya', 'Daniel', 'Priya'],
  lines: [
    {
      speaker: 0,
      time: '0:05',
      text: 'Thanks for joining. First item: the onboarding emails.',
    },
    {
      speaker: 2,
      time: '0:11',
      text: 'Open rates went up after we shortened the subject lines.',
    },
    {
      speaker: 1,
      time: '0:19',
      text: 'Then let’s shorten the welcome email too and compare in two weeks.',
    },
    {
      speaker: 0,
      time: '0:26',
      text: 'Good. Priya, can you set up the test?',
    },
  ],
  playing: 1,
  insight: {
    title: 'Action Items',
    kind: 'checks',
    items: [
      { text: 'Priya sets up the welcome email test' },
      { text: 'Daniel writes a shorter welcome email' },
      { text: 'Review the results in two weeks' },
    ],
  },
};

export const VOICE_MEMO_EXAMPLE: TranscriptExample = {
  file: 'New Recording 27.m4a',
  source: 'file',
  language: 'English',
  duration: '4:16',
  speakers: ['Alex'],
  lines: [
    {
      speaker: 0,
      time: '0:00',
      text: 'Okay, notes for the trip so I don’t forget.',
    },
    {
      speaker: 0,
      time: '0:04',
      text: 'Book the train to Lisbon before Friday, prices go up after that.',
    },
    {
      speaker: 0,
      time: '0:12',
      text: 'Ask Sam if we can borrow the big suitcase.',
    },
    {
      speaker: 0,
      time: '0:18',
      text: 'And find a restaurant for Saturday, somewhere near the river.',
    },
  ],
  playing: 1,
  insight: {
    title: 'Action Items',
    kind: 'checks',
    items: [
      { text: 'Book the train to Lisbon before Friday' },
      { text: 'Ask Sam about the big suitcase' },
      { text: 'Find a restaurant near the river for Saturday' },
    ],
  },
};

export const INTERVIEW_EXAMPLE: TranscriptExample = {
  file: 'interview-dr-chen-final.m4a',
  source: 'file',
  language: 'English',
  duration: '52:31',
  speakers: ['Sarah', 'Dr. Chen'],
  lines: [
    {
      speaker: 0,
      time: '0:04',
      text: 'When did you first notice the change in the bird population?',
    },
    {
      speaker: 1,
      time: '0:10',
      text: 'About ten years ago. Species that used to leave in autumn started staying.',
    },
    {
      speaker: 0,
      time: '0:19',
      text: 'What do you think is behind it?',
    },
    {
      speaker: 1,
      time: '0:23',
      text: 'Milder winters, mostly. They simply don’t need to fly south anymore.',
    },
  ],
  playing: 1,
  insight: {
    title: 'Key quotes',
    kind: 'chapters',
    items: [
      {
        time: '0:10',
        text: 'Species that used to leave in autumn started staying',
      },
      { time: '0:23', text: 'They simply don’t need to fly south anymore' },
      { time: '17:48', text: 'We count every spring, the same way since 1998' },
    ],
  },
};

export const YOUTUBE_SUMMARY_EXAMPLE: TranscriptExample = {
  file: 'Why sleep matters more than you think',
  translateFile: true,
  source: 'youtube',
  language: 'English',
  duration: '22:48',
  speakers: ['Dr. Reyes'],
  lines: [
    {
      speaker: 0,
      time: '0:00',
      text: 'Most adults need seven to nine hours of sleep, and most of us get less.',
    },
    {
      speaker: 0,
      time: '0:07',
      text: 'While you sleep, your brain sorts what you learned during the day.',
    },
    {
      speaker: 0,
      time: '8:15',
      text: 'One short night is enough to make it harder to focus the next day.',
    },
    {
      speaker: 0,
      time: '15:40',
      text: 'The simplest fix is to go to bed at the same time every night.',
    },
  ],
  playing: 1,
  insight: {
    title: 'Summary',
    kind: 'bullets',
    items: [
      { text: 'Adults need seven to nine hours of sleep' },
      { text: 'Sleep helps the brain store what you learned' },
      { text: 'A regular bedtime is the easiest way to sleep better' },
    ],
  },
};

export const NOTE_TAKER_EXAMPLE: TranscriptExample = {
  file: 'Client call: website redesign',
  translateFile: true,
  source: 'recording',
  language: 'English',
  duration: '28:36',
  speakers: ['Alex', 'Hannah'],
  lines: [
    {
      speaker: 1,
      time: '0:05',
      text: 'The main goal is getting more people to book a demo from the homepage.',
    },
    {
      speaker: 0,
      time: '0:12',
      text: 'Then let’s put the demo button at the top and cut the page in half.',
    },
    {
      speaker: 1,
      time: '0:19',
      text: 'Love it. Can we see a first version before the end of the month?',
    },
    {
      speaker: 0,
      time: '0:25',
      text: 'Yes, I’ll send the first design by the twenty-fifth.',
    },
  ],
  playing: 1,
  insight: {
    title: 'Action Items',
    kind: 'checks',
    items: [
      { text: 'Alex sends the first homepage design by the 25th' },
      { text: 'Move the demo button to the top of the page' },
      { text: 'Hannah shares last quarter’s demo numbers' },
    ],
  },
};
